import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, AlertTriangle } from "lucide-react";

// Worker: hostato via CDN cloudflare, sempre online e cached dai browser.
// Usa la stessa versione di pdfjs-dist installata (verificata: 5.4.296)
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

/**
 * PDF viewer inline basato su react-pdf/PDF.js.
 * Props:
 *  - fileUrl: URL (blob: o https:) del PDF da visualizzare
 *  - onError: callback opzionale in caso di errore di caricamento
 */
export function PdfPreview({ fileUrl, onError }) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loadError, setLoadError] = useState(null);
  const containerRef = useRef(null);

  // Reset quando cambia il file
  useEffect(() => {
    setPageNumber(1);
    setLoadError(null);
  }, [fileUrl]);

  // Larghezza responsiva: adatta al container su ogni re-render
  const [containerWidth, setContainerWidth] = useState(900);
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      setContainerWidth(Math.max(300, Math.min(w - 32, 1200)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleLoadSuccess = ({ numPages: np }) => {
    setNumPages(np);
    setLoadError(null);
  };

  const handleLoadError = (err) => {
    setLoadError(err?.message || "Impossibile caricare il PDF");
    onError?.(err);
  };

  return (
    <div className="w-full h-full flex flex-col bg-slate-100" data-testid="pdf-preview">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-slate-200 flex-shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          disabled={pageNumber <= 1 || !numPages}
          data-testid="pdf-prev-page"
          className="h-8 px-2"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-xs text-slate-600 tabular-nums min-w-[70px] text-center" data-testid="pdf-page-info">
          {numPages ? `${pageNumber} / ${numPages}` : "—"}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setPageNumber((p) => Math.min(numPages || p, p + 1))}
          disabled={pageNumber >= numPages || !numPages}
          data-testid="pdf-next-page"
          className="h-8 px-2"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div className="mx-2 h-5 w-px bg-slate-300" />
        <Button
          size="sm"
          variant="outline"
          onClick={() => setScale((s) => Math.max(0.5, s - 0.2))}
          data-testid="pdf-zoom-out"
          className="h-8 px-2"
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
        <span className="text-xs text-slate-600 tabular-nums min-w-[50px] text-center" data-testid="pdf-zoom-level">
          {Math.round(scale * 100)}%
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setScale((s) => Math.min(3, s + 0.2))}
          data-testid="pdf-zoom-in"
          className="h-8 px-2"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
      </div>

      {/* Canvas area */}
      <div ref={containerRef} className="flex-1 overflow-auto p-4 flex items-start justify-center">
        {loadError ? (
          <div className="flex flex-col items-center gap-3 text-slate-600 text-sm mt-8" data-testid="pdf-load-error">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
            <p className="font-medium">Errore caricamento PDF</p>
            <p className="text-xs text-slate-500 max-w-md text-center">{loadError}</p>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 hover:text-blue-900 underline text-xs"
            >
              Apri in nuova scheda
            </a>
          </div>
        ) : (
          <Document
            file={fileUrl}
            onLoadSuccess={handleLoadSuccess}
            onLoadError={handleLoadError}
            loading={
              <div className="flex items-center gap-2 text-slate-500 text-sm mt-8">
                <Loader2 className="h-5 w-5 animate-spin" /> Caricamento PDF…
              </div>
            }
            className="shadow-md"
          >
            <Page
              pageNumber={pageNumber}
              width={containerWidth * scale / 1.2}
              renderAnnotationLayer
              renderTextLayer
            />
          </Document>
        )}
      </div>
    </div>
  );
}

export default PdfPreview;
